import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, catchError, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthResponse, AuthUser, LoginCredentials } from '../model/login.model';
import { PendingRequestsService } from '../../../core/services/pending-requests.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly TOKEN_KEY = 'auth_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly USER_KEY = 'auth_user';

  // Signals para el estado de autenticación
  private currentUserSignal = signal<AuthUser | null>(this.getUserFromStorage());
  private isAuthenticatedSignal = signal<boolean>(this.hasToken());
  
  // Señales de solo lectura
  readonly currentUser = this.currentUserSignal.asReadonly();
  readonly isAuthenticated = this.isAuthenticatedSignal.asReadonly();
  

  // Computed signals
  readonly isAdmin = computed(() => this.currentUser()?.role === 'admin');
  readonly userName = computed(() => this.currentUser()?.name || '');
  readonly menuIds = computed(() => (this.currentUser()?.menuIds || []));
  readonly systemIds = computed(() => this.currentUser()?.systemIds || []);

  private pendingRequestsService = inject(PendingRequestsService);

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    // Verifica el token al iniciar
    this.checkAuthStatus();
  }

  /**
   * Inicia sesión del usuario
   */
  login(credentials: LoginCredentials): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${environment.apiUrl}/UsersManagement/Login`, credentials)
      .pipe(
        tap(response => this.handleAuthSuccess(response)),
        catchError(error => {
          console.error('Error en login:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Cierra sesión del usuario
   */
  logout(): void {
    // Cancelar todas las peticiones pendientes antes de cerrar sesión
    this.pendingRequestsService.cancelAll();

    this.clearAuthData();
    this.currentUserSignal.set(null);
    this.isAuthenticatedSignal.set(false);
    this.router.navigate(['/login']);
  }

  /**
   * Obtiene el token actual
   */
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Obtiene el refresh token
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  /**
   * Verifica si el usuario está autenticado
   */
  checkAuthStatus(): boolean {
    const token = this.getToken();
    const user = this.getUserFromStorage();

    if (token && user) {
      this.isAuthenticatedSignal.set(true);
      this.currentUserSignal.set(user);
      return true;
    }

    this.isAuthenticatedSignal.set(false);
    this.currentUserSignal.set(null);
    return false;
  }

  /**
   * Maneja el éxito de autenticación
   */
  private handleAuthSuccess(response: AuthResponse): void {
    this.saveToken(response.data);

    if (response.data) {
      this.saveRefreshToken(response.data);
    }

    const user: AuthUser = {
      ...response.extra,
      menuIds: this.parseCommaSeparatedToNumbers(response.menus),
      systemIds: this.parseCommaSeparatedToNumbers(response.systems)
    };

    this.saveUserToStorage(user);
    this.currentUserSignal.set(user);
    this.isAuthenticatedSignal.set(true);
  }

  private parseCommaSeparatedToNumbers(value: string | undefined): number[] {
    if (!value || typeof value !== 'string') return [];
    return value.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
  }

  /**
   * Guarda el token en localStorage
   */
  private saveToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  /**
   * Guarda el refresh token
   */
  private saveRefreshToken(refreshToken: string): void {
    localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
  }

  /**
   * Guarda el usuario en localStorage
   */
  private saveUserToStorage(user: AuthUser): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  /**
   * Obtiene el usuario de localStorage
   */
  private getUserFromStorage(): AuthUser | null {

    const userJson = localStorage.getItem(this.USER_KEY);
    if (!userJson) return null;
    try {
      return JSON.parse(userJson);
    } catch (error) {
      console.error('AuthService: Error parsing user from storage:', error);
      return null;
    }
  }

  /**
   * Verifica si existe un token
   */
  private hasToken(): boolean {
    return !!localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Limpia todos los datos de autenticación
   */
  private clearAuthData(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
  }

  /**
   * Verifica si el usuario tiene un rol específico
   */
  hasRole(role: string): boolean {
    return this.currentUser()?.role === role;
  }

  /**
   * Verifica si el usuario tiene alguno de los roles
   */
  hasAnyRole(roles: string[]): boolean {
    const userRole = this.currentUser()?.role;
    return userRole ? roles.includes(userRole) : false;
  }
}